if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.slots = [];
        this.items = [];
        this.discountCents = 0;
        this.minCheckoutItems = 2;
        this.moneyFormat = '${{amount}}';
        this.activeSlotKey = '';
        this.activeSlotIndex = 0;
        this.isCheckingOut = false;
        this.onAddProduct = this.onAddProduct.bind(this);
        this.onCheckout = this.onCheckout.bind(this);
        this.onSlotClick = this.onSlotClick.bind(this);
        this.onSlotKeydown = this.onSlotKeydown.bind(this);
        this.onRemoveClick = this.onRemoveClick.bind(this);
        this.onSummaryClick = this.onSummaryClick.bind(this);
      }

      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || this.moneyFormat;
        this.discountCents = Number.parseInt(this.dataset.bundleDiscount || '0', 10) || 0;
        this.minCheckoutItems = Number.parseInt(this.dataset.minItems || '2', 10) || 2;
        this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
        this.slots = Array.from(this.querySelectorAll('[data-bst-bundle-slot]'));
        this.itemsListEl = this.querySelector('[data-bst-bundle-items]');
        this.countEl = this.querySelector('[data-bst-bundle-count]');
        this.subtotalEl = this.querySelector('[data-bst-bundle-subtotal]');
        this.discountEl = this.querySelector('[data-bst-bundle-discount]');
        this.totalEl = this.querySelector('[data-bst-bundle-total]');
        this.checkoutBtn = this.querySelector('[data-bst-bundle-checkout]');
        this.liveRegion = this.querySelector('[data-bst-bundle-live]');

        this.items = this.slots.map(() => null);
        this.checkoutBtn?.addEventListener('click', this.onCheckout);
        this.itemsListEl?.addEventListener('click', this.onSummaryClick);
        document.addEventListener('bst:bundle:add', this.onAddProduct);

        this.slots.forEach((slotEl) => {
          const selectEl = slotEl.querySelector('[data-bst-bundle-slot-select]') || slotEl;
          selectEl.addEventListener('click', this.onSlotClick);
          selectEl.addEventListener('keydown', this.onSlotKeydown);
          slotEl.querySelector('[data-bst-slot-remove]')?.addEventListener('click', this.onRemoveClick);
        });

        const initialSlotIndex = this.getInitialSlotIndex();
        const initialSlotKey = this.slots[initialSlotIndex]?.dataset.slotKey || '';
        this.selectSlot(initialSlotKey, { slotIndex: initialSlotIndex, announce: false });
        this.updateSummary();
        this.updateHeaderOffset();
      }

      updateHeaderOffset() {
        const header = document.querySelector('.section-header');
        if (!header) return;

        document.documentElement.style.setProperty('--bst-header-height', `${header.offsetHeight}px`);
      }

      getScrollOffset() {
        const rootStyles = getComputedStyle(document.documentElement);
        const parsedBstHeader = Number.parseInt(rootStyles.getPropertyValue('--bst-header-height'), 10);
        if (Number.isFinite(parsedBstHeader) && parsedBstHeader > 0) {
          return parsedBstHeader + 16;
        }

        const parsedHeader = Number.parseInt(rootStyles.getPropertyValue('--header-height'), 10);
        if (Number.isFinite(parsedHeader) && parsedHeader > 0) {
          return parsedHeader + 16;
        }

        const header = document.querySelector('.section-header');
        if (header) return header.offsetHeight + 16;

        return 88;
      }

      getScrollBehavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      scrollToTarget(target) {
        if (!target) return;

        this.updateHeaderOffset();

        const behavior = this.getScrollBehavior();
        const offset = this.getScrollOffset();
        const top = target.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: Math.max(0, top),
          behavior,
        });

        const alignTarget = () => {
          this.updateHeaderOffset();
          const delta = target.getBoundingClientRect().top - this.getScrollOffset();
          if (Math.abs(delta) > 2) {
            window.scrollBy({ top: delta, behavior: 'auto' });
          }
        };

        if (behavior === 'smooth') {
          window.setTimeout(alignTarget, 450);
          return;
        }

        window.requestAnimationFrame(alignTarget);
      }

      disconnectedCallback() {
        document.removeEventListener('bst:bundle:add', this.onAddProduct);
        this.checkoutBtn?.removeEventListener('click', this.onCheckout);
        this.itemsListEl?.removeEventListener('click', this.onSummaryClick);
        this.slots.forEach((slotEl) => {
          const selectEl = slotEl.querySelector('[data-bst-bundle-slot-select]') || slotEl;
          selectEl.removeEventListener('click', this.onSlotClick);
          selectEl.removeEventListener('keydown', this.onSlotKeydown);
          slotEl.querySelector('[data-bst-slot-remove]')?.removeEventListener('click', this.onRemoveClick);
        });
      }

      getActiveSlotKey() {
        return this.activeSlotKey;
      }

      getInitialSlotIndex() {
        const firstEmptyIndex = this.items.findIndex((item) => !item);
        return firstEmptyIndex >= 0 ? firstEmptyIndex : 0;
      }

      getNextEmptySlotIndex(fromSlotIndex) {
        for (let index = fromSlotIndex + 1; index < this.slots.length; index += 1) {
          if (!this.items[index]) return index;
        }

        for (let index = 0; index < fromSlotIndex; index += 1) {
          if (!this.items[index]) return index;
        }

        return -1;
      }

      onSlotClick(event) {
        if (event.target.closest('[data-bst-slot-remove]')) return;
        const slotEl = event.currentTarget.closest('[data-bst-bundle-slot]');
        const slotIndex = this.slots.indexOf(slotEl);
        const slotKey = slotEl?.dataset.slotKey;
        if (!slotKey || slotIndex < 0) return;
        this.selectSlot(slotKey, { slotIndex, scrollToShop: true });
      }

      onRemoveClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const slotEl = event.currentTarget.closest('[data-bst-bundle-slot]');
        if (!slotEl) return;

        const slotIndex = this.slots.indexOf(slotEl);
        if (slotIndex < 0 || !this.items[slotIndex]) return;

        this.removeItem(slotIndex);
      }

      onSummaryClick(event) {
        const removeBtn = event.target.closest('[data-bst-summary-remove]');
        if (!removeBtn) return;

        event.preventDefault();
        const slotIndex = Number.parseInt(removeBtn.dataset.bstSummaryRemove || '', 10);
        if (Number.isNaN(slotIndex) || !this.items[slotIndex]) return;

        this.removeItem(slotIndex);
      }

      removeItem(slotIndex) {
        const item = this.items[slotIndex];
        if (!item) return;

        this.clearSlot(slotIndex);
        this.updateSummary();
        this.announce(`${item.title} removed from your bundle.`);

        const slotKey = this.slots[slotIndex]?.dataset.slotKey;
        if (slotKey) {
          this.selectSlot(slotKey, { slotIndex, announce: false, scrollToShop: true });
        }
      }

      clearSlot(index) {
        const slotEl = this.slots[index];
        if (!slotEl) return;

        this.items[index] = null;
        slotEl.classList.remove('is-filled');
        slotEl.classList.add('is-empty');

        const titleEl = slotEl.querySelector('[data-bst-slot-title]');
        const priceEl = slotEl.querySelector('[data-bst-slot-price]');
        const imageEl = slotEl.querySelector('[data-bst-slot-image]');
        const statusEl = slotEl.querySelector('[data-bst-slot-status]');
        const removeBtn = slotEl.querySelector('[data-bst-slot-remove]');

        if (titleEl) titleEl.textContent = slotEl.dataset.placeholderTitle || '';
        if (priceEl) priceEl.textContent = slotEl.dataset.placeholderPrice || '';
        if (statusEl) {
          statusEl.textContent = slotEl.dataset.stepNumber || '';
          statusEl.classList.remove('is-complete');
        }
        if (removeBtn) removeBtn.hidden = true;

        this.setEmptySlotImage(imageEl);
      }

      onSlotKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.onSlotClick(event);
      }

      selectSlot(slotKey, options = {}) {
        let slotIndex = options.slotIndex;
        if (slotIndex == null || slotIndex < 0) {
          slotIndex = this.slots.findIndex((slotEl) => slotEl.dataset.slotKey === slotKey);
        }
        if (slotIndex < 0) return;

        const activeSlot = this.slots[slotIndex];
        slotKey = activeSlot?.dataset.slotKey || slotKey;
        if (!slotKey) return;

        this.activeSlotKey = slotKey;
        this.activeSlotIndex = slotIndex;
        this.slots.forEach((slotEl, index) => {
          const isActive = index === slotIndex;
          const selectEl = slotEl.querySelector('[data-bst-bundle-slot-select]') || slotEl;
          slotEl.classList.toggle('is-active', isActive);
          selectEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        document.dispatchEvent(
          new CustomEvent('bst:bundle:slot-select', {
            bubbles: true,
            detail: {
              slotKey,
              scrollToShop: Boolean(options.scrollToShop),
            },
          })
        );

        if (options.announce !== false) {
          const stepLabel = activeSlot?.dataset.stepLabel || slotKey;
          this.announce(`Showing products for ${stepLabel}.`);
        }
      }

      onAddProduct(event) {
        const detail = event.detail || {};
        const complete = typeof detail.onComplete === 'function' ? detail.onComplete : null;

        const slotIndex = this.resolveSlotIndex(detail);
        if (slotIndex < 0) {
          this.announce('All bundle steps are filled. Replace a selection to add a different product.');
          complete?.(false);
          return;
        }

        if (!detail.variantId) {
          this.announce('Unable to add this product. Please choose an available option.');
          complete?.(false);
          return;
        }

        const item = {
          slotIndex,
          slotKey: detail.slotKey || '',
          variantId: detail.variantId,
          productId: detail.productId,
          title: detail.title,
          price: detail.price,
          imageUrl: detail.imageUrl || '',
          stepLabel: this.slots[slotIndex]?.dataset.stepLabel || '',
        };

        this.items[slotIndex] = item;
        this.renderSlot(slotIndex, item);
        this.updateSummary();
        this.announce(`${detail.title} added to your bundle.`);

        const nextSlotIndex = this.getNextEmptySlotIndex(slotIndex);
        if (nextSlotIndex >= 0) {
          const nextSlotKey = this.slots[nextSlotIndex]?.dataset.slotKey || '';
          this.selectSlot(nextSlotKey, { slotIndex: nextSlotIndex, announce: false });
        }

        this.scheduleScrollToSlots(slotIndex);

        complete?.(true);
      }

      scheduleScrollToSlots(slotIndex) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.scrollToSlots(slotIndex);
          });
        });
      }

      scrollToSlots(slotIndex) {
        const slotsEl =
          this.querySelector('.bst-bundle-builder__slots-wrap') ||
          this.querySelector('.bst-bundle-builder__slots');
        const slotEl = Number.isInteger(slotIndex) ? this.slots[slotIndex] : null;
        const target = slotsEl || slotEl;

        if (!target) return;

        this.scrollToTarget(target);
      }

      resolveSlotIndex(detail) {
        const slotKey = detail.slotKey || '';

        if (this.activeSlotIndex >= 0 && this.slots[this.activeSlotIndex]) {
          const activeSlot = this.slots[this.activeSlotIndex];
          if (
            (!slotKey || activeSlot.dataset.slotKey === slotKey) &&
            !this.items[this.activeSlotIndex]
          ) {
            return this.activeSlotIndex;
          }
        }

        if (slotKey) {
          const keyedEmpty = this.slots.findIndex(
            (slotEl, index) => slotEl.dataset.slotKey === slotKey && !this.items[index]
          );
          if (keyedEmpty >= 0) return keyedEmpty;

          const keyedFilled = this.slots.findIndex((slotEl) => slotEl.dataset.slotKey === slotKey);
          if (keyedFilled >= 0) return keyedFilled;
        }

        const firstEmpty = this.items.findIndex((item) => !item);
        if (firstEmpty >= 0) return firstEmpty;

        return -1;
      }

      renderSlot(index, item) {
        const slotEl = this.slots[index];
        if (!slotEl || !item) return;

        slotEl.classList.add('is-filled');
        slotEl.classList.remove('is-empty');

        const titleEl = slotEl.querySelector('[data-bst-slot-title]');
        const priceEl = slotEl.querySelector('[data-bst-slot-price]');
        const imageEl = slotEl.querySelector('[data-bst-slot-image]');
        const statusEl = slotEl.querySelector('[data-bst-slot-status]');
        const removeBtn = slotEl.querySelector('[data-bst-slot-remove]');

        if (titleEl) titleEl.textContent = item.title;
        if (priceEl) priceEl.textContent = this.formatMoney(item.price);
        if (statusEl) {
          statusEl.textContent = '✓';
          statusEl.classList.add('is-complete');
        }
        if (removeBtn) removeBtn.hidden = false;

        if (imageEl) {
          if (item.imageUrl) {
            imageEl.innerHTML = `<img src="${item.imageUrl}" alt="" loading="lazy" decoding="async" width="64" height="64">`;
          } else {
            this.setEmptySlotImage(imageEl);
          }
        }
      }

      setEmptySlotImage(imageEl) {
        const placeholderUrl = this.dataset.emptySlotImage;
        if (!placeholderUrl) {
          imageEl.innerHTML = '';
          return;
        }

        imageEl.innerHTML = `<img class="bst-bundle-builder__slot-media-placeholder" src="${placeholderUrl}" alt="" width="64" height="64" decoding="async">`;
      }

      updateSummary() {
        const filledItems = this.items.filter(Boolean);
        const subtotal = filledItems.reduce((sum, item) => sum + (item.price || 0), 0);
        const discount = Math.min(this.discountCents, subtotal);
        const total = Math.max(subtotal - discount, 0);

        if (this.countEl) {
          const count = filledItems.length;
          this.countEl.textContent = count === 1 ? '1 item' : `${count} items`;
        }

        if (this.subtotalEl) {
          this.subtotalEl.textContent = filledItems.length ? this.formatMoney(subtotal) : this.formatMoney(0);
        }
        if (this.discountEl) {
          this.discountEl.textContent = discount > 0 ? `–${this.formatMoney(discount)}` : '–$0';
        }
        if (this.totalEl) {
          this.totalEl.textContent = filledItems.length ? this.formatMoney(total) : this.formatMoney(0);
        }

        if (this.itemsListEl) {
          this.itemsListEl.innerHTML = filledItems
            .map(
              (item) => `
                <li class="bst-bundle-builder__summary-item">
                  <span class="bst-bundle-builder__summary-item-title">${this.escapeHtml(item.title)}</span>
                  <span class="bst-bundle-builder__summary-item-price">${this.formatMoney(item.price)}</span>
                  <button
                    type="button"
                    class="bst-bundle-builder__summary-item-remove"
                    data-bst-summary-remove="${item.slotIndex}"
                    aria-label="Remove ${this.escapeHtml(item.title)}"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </li>
              `
            )
            .join('');
        }

        if (this.checkoutBtn) {
          const canCheckout = filledItems.length >= this.minCheckoutItems;

          if (!this.isCheckingOut) {
            this.checkoutBtn.disabled = !canCheckout;
            this.checkoutBtn.setAttribute('aria-disabled', canCheckout ? 'false' : 'true');
            this.checkoutBtn.classList.toggle('is-locked', !canCheckout);
          }
        }
      }

      setCheckoutLoading(isLoading) {
        if (!this.checkoutBtn) return;

        this.checkoutBtn.classList.toggle('loading', isLoading);
        this.checkoutBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');

        if (isLoading) {
          this.checkoutBtn.disabled = true;
          this.checkoutBtn.setAttribute('aria-disabled', 'true');
          this.checkoutBtn.classList.remove('is-locked');
        }
      }

      async onCheckout(event) {
        event.preventDefault();
        const filledItems = this.items.filter(Boolean);
        if (filledItems.length < this.minCheckoutItems || !this.checkoutBtn || this.isCheckingOut) return;

        this.isCheckingOut = true;
        this.setCheckoutLoading(true);

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: filledItems.map((item) => ({
                id: Number.parseInt(String(item.variantId), 10),
                quantity: 1,
              })),
            }),
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.description || data.message || 'Unable to add bundle to cart.');
          }

          window.location.assign(this.checkoutUrl || '/checkout');
        } catch (error) {
          this.isCheckingOut = false;
          this.setCheckoutLoading(false);
          this.announce(error.message || 'Unable to add bundle to cart.');
          this.updateSummary();
        }
      }

      formatMoney(cents) {
        const dollars = String(Math.round(Number(cents || 0) / 100));
        return this.moneyFormat
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, dollars)
          .replace(/\{\{\s*amount\s*\}\}/, dollars);
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      announce(message) {
        if (!this.liveRegion || !message) return;
        this.liveRegion.textContent = '';
        window.requestAnimationFrame(() => {
          this.liveRegion.textContent = message;
        });
      }
    }
  );
}

if (!customElements.get('bst-bundle-card')) {
  customElements.define(
    'bst-bundle-card',
    class BstBundleCard extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this.isAdding = false;
        this.addResetTimer = null;
        this.onOptionChange = this.onOptionChange.bind(this);
        this.onAddClick = this.onAddClick.bind(this);
      }

      connectedCallback() {
        this.variants = this.loadVariants();
        this.variantInput = this.querySelector('[data-bst-bundle-variant-id]');
        this.addBtn = this.querySelector('[data-bst-bundle-add]');
        this.priceEl = this.querySelector('[data-bst-bundle-card-price-value]')
          || this.querySelector('.bst-product-cell__current-price');
        this.priceFromEl = this.querySelector('.bst-product-cell__from');
        this.imageEl = this.querySelector('.bst-product-cell__image--primary');

        this.querySelectorAll('[data-bst-bundle-option]').forEach((select) => {
          select.addEventListener('change', this.onOptionChange);
        });

        this.addBtn?.addEventListener('click', this.onAddClick);
        this.syncVariant();
      }

      disconnectedCallback() {
        this.querySelectorAll('[data-bst-bundle-option]').forEach((select) => {
          select.removeEventListener('change', this.onOptionChange);
        });
        this.addBtn?.removeEventListener('click', this.onAddClick);
        if (this.addResetTimer) {
          window.clearTimeout(this.addResetTimer);
          this.addResetTimer = null;
        }
      }

      loadVariants() {
        const dataEl = this.querySelector('[data-bst-bundle-variants-json]');
        if (!dataEl) return [];

        try {
          return JSON.parse(dataEl.textContent);
        } catch (error) {
          return [];
        }
      }

      getSelectedOptions() {
        return Array.from(this.querySelectorAll('[data-bst-bundle-option]')).map((select) => select.value);
      }

      findVariant() {
        const selectedOptions = this.getSelectedOptions();
        if (!selectedOptions.length) {
          return this.variants.find((variant) => variant.available) || this.variants[0] || null;
        }

        return (
          this.variants.find((variant) =>
            //variant.options.every((option, index) => option === selectedOptions[index])
            selectedOptions.every((value, index) => variant.options[index] === value)
          ) || null
        );
      }

      getCurrentVariant() {
        if (this.variants.length) {
          return this.findVariant();
        }

        const variantId = this.variantInput?.value || this.dataset.defaultVariantId;
        if (!variantId) return null;

        return {
          id: Number.parseInt(String(variantId), 10),
          available: this.dataset.defaultVariantAvailable !== 'false',
          price: Number.parseInt(this.dataset.defaultVariantPrice || '0', 10) || 0,
          featured_image: this.dataset.productImage || null,
        };
      }

      setAddingState(isAdding) {
        this.isAdding = isAdding;
        if (!this.addBtn) return;

        this.addBtn.classList.toggle('loading', isAdding);
        this.addBtn.setAttribute('aria-busy', isAdding ? 'true' : 'false');

        if (isAdding) {
          this.addBtn.disabled = true;
          this.addBtn.setAttribute('aria-disabled', 'true');
          return;
        }

        const variant = this.getCurrentVariant();
        const isAvailable = Boolean(variant?.available);
        this.addBtn.disabled = !isAvailable;
        this.addBtn.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
      }

      onOptionChange(event) {
        const select = event.target;
        const position = select.dataset.bstBundleOption;
        const valueEl = this.querySelector(`[data-bst-bundle-option-value="${position}"]`);
        if (valueEl) valueEl.textContent = select.value;
        this.syncVariant();
      }

      syncVariant() {
        const variant = this.getCurrentVariant();

        if (!variant) {
          if (this.addBtn && !this.isAdding) {
            this.addBtn.disabled = true;
            this.addBtn.setAttribute('aria-disabled', 'true');
          }
          return;
        }

        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        if (this.priceEl && variant.price != null) {
          this.priceEl.textContent = this.formatMoney(variant.price);
        }

        if (this.priceFromEl) {
          this.priceFromEl.hidden = true;
        }

        if (variant.featured_image && this.imageEl) {
          this.imageEl.src = variant.featured_image;
          this.imageEl.removeAttribute('srcset');
        }

        if (this.addBtn && !this.isAdding) {
          this.addBtn.disabled = !variant.available;
          this.addBtn.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        }
      }

      onAddClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isAdding) return;

        const variant = this.getCurrentVariant();
        if (!variant || !variant.available) return;

        this.setAddingState(true);

        const imageUrl =
          this.imageEl?.currentSrc || this.imageEl?.src || variant.featured_image || this.dataset.productImage || '';

        let completed = false;
        const finish = (success) => {
          if (completed) return;
          completed = true;

          if (this.addResetTimer) {
            window.clearTimeout(this.addResetTimer);
            this.addResetTimer = null;
          }

          window.setTimeout(() => {
            this.setAddingState(false);
          }, success ? 350 : 0);
        };

        this.addResetTimer = window.setTimeout(() => finish(false), 2500);

        document.dispatchEvent(
          new CustomEvent('bst:bundle:add', {
            bubbles: true,
            detail: {
              productId: this.dataset.productId,
              variantId: variant.id,
              title: this.dataset.productTitle,
              price: variant.price,
              imageUrl,
              slotKey: this.dataset.slotKey || '',
              onComplete: finish,
            },
          })
        );
      }

      formatMoney(cents) {
        const builder = document.querySelector('bundle-builder');
        const format = builder?.dataset.moneyFormat || '${{amount}}';
        const dollars = String(Math.round(Number(cents || 0) / 100));
        return format
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, dollars)
          .replace(/\{\{\s*amount\s*\}\}/, dollars);
      }
    }
  );
}

if (!customElements.get('bundle-shop')) {
  customElements.define(
    'bundle-shop',
    class BundleShop extends HTMLElement {
      constructor() {
        super();
        this.items = [];
        this.emptyEl = null;
        this.onSlotSelect = this.onSlotSelect.bind(this);
      }

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-bst-bundle-shop-item]'));
        this.emptyEl = this.querySelector('[data-bst-bundle-shop-empty]');
        document.addEventListener('bst:bundle:slot-select', this.onSlotSelect);

        const builder = document.querySelector('bundle-builder');
        const initialSlotKey =
          builder?.getActiveSlotKey?.() ||
          this.items.find((item) => item.dataset.slotKey)?.dataset.slotKey ||
          '';

        if (initialSlotKey) {
          this.filterBySlot(initialSlotKey);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('bst:bundle:slot-select', this.onSlotSelect);
      }

      filterBySlot(slotKey) {
        let visibleCount = 0;
        const hasCategorizedItems = this.items.some((item) => item.dataset.slotKey);

        this.items.forEach((item) => {
          const matches = !hasCategorizedItems || item.dataset.slotKey === slotKey;
          item.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        if (this.emptyEl) {
          this.emptyEl.hidden = visibleCount > 0;
        }

        this.resetGridScroll();
      }

      resetGridScroll() {
        const grid = this.querySelector('.bst-bundle-shop__grid');
        if (!grid) return;

        window.requestAnimationFrame(() => {
          grid.scrollLeft = 0;

          const firstVisible = this.items.find((item) => !item.hidden);
          if (firstVisible && grid.scrollWidth > grid.clientWidth) {
            const offset = firstVisible.offsetLeft - grid.offsetLeft;
            grid.scrollLeft = Math.max(0, offset);
          }
        });
      }

      onSlotSelect(event) {
        const slotKey = event.detail?.slotKey;
        if (!slotKey) return;
        this.filterBySlot(slotKey);

        if (event.detail?.scrollToShop) {
          window.requestAnimationFrame(() => {
            this.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'start',
            });

            window.setTimeout(() => {
              this.resetGridScroll();
            }, 350);
          });
        }
      }
    }
  );
}
(function () {
  var Ctor = customElements.get('bst-bundle-card');
  if (!Ctor || Ctor.prototype.__bstComparePricePatched) return;
  var originalSyncVariant = Ctor.prototype.syncVariant;
  Ctor.prototype.__bstComparePricePatched = true;
  Ctor.prototype.syncVariant = function () {
    originalSyncVariant.call(this);
    if (!this.variants || !this.variants.length) return; // static cards already render this correctly, leave them alone
    var compareEl = this.__comparePriceEl;
    if (!compareEl) {
      compareEl = this.querySelector('.bst-product-cell__compare-price');
      if (!compareEl) {
        var priceEl = this.priceEl || this.querySelector('.bst-product-cell__current-price');
        if (priceEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'bst-product-cell__compare-price bst-text-gray-600';
          priceEl.insertAdjacentElement('afterend', compareEl);
        }
      }
      this.__comparePriceEl = compareEl;
    }
    if (!compareEl) return;
    var variant = this.getCurrentVariant();
    if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
      compareEl.hidden = false;
      compareEl.textContent = this.formatMoney(variant.compare_at_price);
    } else {
      compareEl.hidden = true;
      compareEl.textContent = '';
    }
  };

  // Re-run the (now patched) sync on every card that already upgraded
  // before this patch was applied, so the default state gets the
  // strikethrough price too, not just after an option change.
  document.querySelectorAll('bst-bundle-card').forEach(function (card) {
    if (typeof card.syncVariant === 'function') {
      card.syncVariant();
    }
  });
})();
(function () {
  var Ctor = customElements.get('bundle-builder');
  if (!Ctor || Ctor.prototype.__bstDiscountPreviewPatched) return;
  Ctor.prototype.__bstDiscountPreviewPatched = true;
 
  // Ask Shopify to price this exact combination of variants in a throwaway
  // preview cart (not the customer's real cart), so we pick up whatever
  // Automatic Discounts are configured without hardcoding their rules here.
  Ctor.prototype.fetchPreviewCart = async function (lines) {
    var query =
      'mutation BundlePreviewCart($lines: [CartLineInput!]) {' +
      '  cartCreate(input: { lines: $lines }) {' +
      '    cart {' +
      '      id' +
      '      cost {' +
      '        subtotalAmount { amount }' +
      '        totalAmount { amount }' +
      '      }' +
      '      lines(first: 20) {' +
      '        nodes {' +
      '          quantity' +
      '          merchandise {' +
      '            ... on ProductVariant {' +
      '              price { amount }' +
      '              compareAtPrice { amount }' +
      '            }' +
      '          }' +
      '        }' +
      '      }' +
      '    }' +
      '    userErrors { field message }' +
      '  }' +
      '}';
 
    var response = await fetch('/api/unstable/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: { lines: lines } }),
    });
 
    if (!response.ok) return null;
    var payload = await response.json();
    var result = payload && payload.data && payload.data.cartCreate;
    if (!result || (result.userErrors && result.userErrors.length)) return null;
    return result.cart;
  };
 
  Ctor.prototype.renderTotals = function (totals) {
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(totals.subtotal);
    if (this.discountEl) {
      this.discountEl.textContent = totals.savings > 0 ? '–' + this.formatMoney(totals.savings) : '–$0';
    }
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(totals.total);
  };
 
  // Fallback to the old flat, merchant-set discount if the live lookup fails
  // for any reason (network issue, etc.) so the widget never breaks.
  Ctor.prototype.renderFallbackTotals = function (filledItems) {
    var subtotal = filledItems.reduce(function (sum, item) { return sum + (item.price || 0); }, 0);
    var discount = Math.min(this.discountCents, subtotal);
    this.renderTotals({ subtotal: subtotal, total: Math.max(subtotal - discount, 0), savings: discount });
  };
 
  Ctor.prototype.refreshDiscount = async function () {
    var filledItems = this.items.filter(Boolean);
    var myToken = (this.__recalcToken = (this.__recalcToken || 0) + 1);
 
    if (!filledItems.length) {
      this.renderTotals({ subtotal: 0, total: 0, savings: 0 });
      return;
    }
 
    var lines = filledItems.map(function (item) {
      return { merchandiseId: 'gid://shopify/ProductVariant/' + item.variantId, quantity: 1 };
    });
 
    var cart = null;
    try {
      cart = await this.fetchPreviewCart(lines);
    } catch (error) {
      cart = null;
    }
 
    if (myToken !== this.__recalcToken) return; // a newer change superseded this lookup
 
    if (!cart) {
      this.renderFallbackTotals(filledItems);
      return;
    }
 
    var total = Math.round(Number(cart.cost.totalAmount.amount) * 100);
    var regularSubtotal = cart.lines.nodes.reduce(function (sum, line) {
      var compareAt = line.merchandise && line.merchandise.compareAtPrice && line.merchandise.compareAtPrice.amount;
      var price = line.merchandise && line.merchandise.price && line.merchandise.price.amount;
      var unit = (compareAt && Number(compareAt) > Number(price)) ? Number(compareAt) : Number(price);
      return sum + Math.round(unit * 100) * line.quantity;
    }, 0);
 
    this.renderTotals({
      subtotal: regularSubtotal,
      total: total,
      savings: Math.max(regularSubtotal - total, 0),
    });
  };
 
  // Every time updateSummary() runs (an item was added or removed), do the
  // usual instant local update first (so the UI never looks stuck), then
  // kick off the accurate, Shopify-verified recalculation.
  var originalUpdateSummary = Ctor.prototype.updateSummary;
  Ctor.prototype.updateSummary = function () {
    originalUpdateSummary.call(this);
    this.refreshDiscount();
  };
})();